import { EditOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import {
  Button,
  Col,
  Collapse,
  Descriptions,
  FormInstance,
  Row,
  Spin,
  message,
} from "antd";
import { AxiosResponse } from "axios";
import React, { Fragment, RefObject } from "react";
import ReactHTMLParser from "react-html-parser";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import AttachmentsSection from "../components/AttachmentsSection";
import Card from "../components/Card";
import TemplateTypeTag from "../components/TemplateTypeTag";
import { mapContactLevels } from "../components/ContactDetailsView";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import {
  ASSIGN_ATTACHMENT_TO_TEMPLATE,
  GET_EMAIL_TEMPLATE,
  UNASSIGN_ATTACHMENT_FROM_TEMPLATE,
} from "../gql/templates";
import {
  APIError,
  Attachment,
  Template,
  TemplateAttachment,
} from "../helpers/shared-interfaces";
import { request, responseHandler } from "../helpers/utilities";
import { CREATE_ATTACHMENT } from "../gql/general";
import { generateDisplayErrorMessage } from "../helpers/utilities";


const { Panel } = Collapse;

interface State {
  isLoading: boolean;
  template: Template;
  id: string;
  attachments: Attachment[];
  attachmentIds: string[];
  templateAttachments: TemplateAttachment[];
}

interface RouteParams {
  id: string;
}

export default class ViewTemplate extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      attachments: [],
      attachmentIds: [],
      templateAttachments: [],
      template: {
        id: "",
        name: "",
        key: "",
        emailSubject: "",
        templateType: "",
        body: "",
        templateCategory: {
          id: "",
          name: "",
        },
        templateAttachments: [],
        recipientTemplateContactLevels: [],
        recipientTemplateCcContactLevels: [],
      },
    };
    this.reference = React.createRef<FormInstance>();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  getTemplate = async (): Promise<void> => {
    const { id } = this.state;
    this.setState({ isLoading: true });
    const {
      data: { data },
    }: AxiosResponse<{
      data: {
        template: Template;
      };
    }> = await request({ query: GET_EMAIL_TEMPLATE, variables: { id } });
    this.setState({
      template: data.template,
      templateAttachments: data.template.templateAttachments,
      attachments: data.template.templateAttachments.map(
        (templateAttachment: TemplateAttachment) =>
          templateAttachment.attachment,
      ),
      attachmentIds: data.template.templateAttachments.map(
        (templateAttachment: TemplateAttachment) =>
          templateAttachment.attachment.id,
      ),
      isLoading: false,
    });
  };

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getTemplate();
    });
  }

  assignAttachmentToTemplate = async (
    attachmentIds: string[],
    templateId: string,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToTemplate: {
          template: {
            templateAttachments: TemplateAttachment[];
          };
        };
      };
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_TEMPLATE,
      variables: {
        attachmentIds,
        templateId,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({
        isLoading: false,
        templateAttachments:
          data.data.assignAttachmentsToTemplate.template.templateAttachments,
      });
    });
  };

  unassignAttachmentFromTemplate = async (id: string): Promise<void> => {
    const { templateAttachments } = this.state;

    const templateAttachmentToDelete: TemplateAttachment | undefined =
      templateAttachments.find(
        (cra: TemplateAttachment) => cra.attachment.id === id,
      );

    interface Response {
      result: boolean;
      errors: APIError[];
    }

    try {
      this.setState({ isLoading: true });
      const response: AxiosResponse = await request({
        query: UNASSIGN_ATTACHMENT_FROM_TEMPLATE,
        variables: { id: templateAttachmentToDelete?.id },
      });
      const { mutation } = responseHandler<Response>(
        response,
        "unassignAttachmentFromTemplate",
      );

      if (!mutation.result) {
        message.error("Failed to delete attachement");
      } else {
        const attachments = this.state.attachments.filter(
          (attachment) => attachment.id !== id,
        );
        this.setState({
          attachments,
          isLoading: false,
        });
        message.success("Deleted Attachment Successfully");
      }
    } catch (error) {
      message.error(`An error occurred, please refresh and try again later - ${generateDisplayErrorMessage(error)}`);
    }
  };

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    const fieldsToBeValidated: string[] = ["url"];

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");

      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: {
          url,
          name,
        },
      });
      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );
      this.setState(
        (state) => ({
          attachmentIds: [...state.attachmentIds, mutation.attachment.id],
          attachments: [...state.attachments, mutation.attachment],
        }),
        () =>
          this.assignAttachmentToTemplate(
            this.state.attachmentIds,
            this.state.id,
          ),
      );

      this.reference.current &&
        this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  render(): JSX.Element | null {
    const { template, id } = this.state;
    let contactLevel: JSX.Element = <span></span>;
    let ccContactLevel: JSX.Element = <span></span>;
    if (template.recipientTemplateContactLevels.length > 0) {
      contactLevel = mapContactLevels(template.recipientTemplateContactLevels);
    }
    if (template.recipientTemplateCcContactLevels.length > 0) {
      ccContactLevel = mapContactLevels(
        template.recipientTemplateCcContactLevels,
      );
    }
    return (
      <Spin spinning={this.state.isLoading}>
        <Card>
          <Fragment>
            <Row
              style={{
                marginBottom: "24px",
              }}
              align="middle"
            >
              <Col
                style={{
                  textAlign: "left",
                }}
                span={14}
              >
                <div>Template Name</div>
                <div>
                  <span>
                    <h1
                      style={{
                        fontWeight: "bold",
                        fontSize: "25px",
                        marginBottom: "0px",
                      }}
                    >
                      {template.name.toUpperCase()}
                    </h1>
                  </span>
                </div>
              </Col>
              <Col
                style={{
                  textAlign: "right",
                }}
                span={10}
              >
                <span
                  style={{
                    backgroundColor: "#f0f5ff",
                    color: "#1d3764",
                    padding: "4px 10px",
                    marginRight: "8px",
                    display: "inline-block",
                  }}
                >
                  {template.templateCategory?.name || "N/A"}
                </span>
                <TemplateTypeTag text={template.templateType} />
              </Col>
            </Row>

            <Row
              style={{
                marginBottom: "24px",
              }}
            >
              <Col span={24} style={{ textAlign: "right" }}>
                <EntitledSection entitlement={entitlements.Templates.edit}>
                  <NavLink
                    to={`/communication-templates/${id}/edit?name=${template.name}`}
                  >
                    <Button
                      icon={<EditOutlined />}
                      size="large"
                      style={{
                        backgroundColor: "  rgb(29, 55, 100)",
                        color: "white",
                        marginRight: "10px",
                      }}
                    >
                      Edit
                    </Button>
                  </NavLink>
                </EntitledSection>
              </Col>
            </Row>

            <Row
              style={{
                marginBottom: "24px",
              }}
            >
              <Col span={24}>
                <EntitledSection entitlement={entitlements.Templates.view}>
                  <div style={{ marginTop: "20px" }}>
                    <p>{ReactHTMLParser(template.body)}</p>
                  </div>
                </EntitledSection>
              </Col>
            </Row>
            <Row
              style={{
                marginBottom: "24px",
              }}
            >
              <Col span={24}>
                <Collapse>
                  <Panel header="Attachments" key="attachments">
                    <AttachmentsSection
                      reference={this.reference}
                      createAttachment={this.createAttachment}
                      attachments={this.state.attachments}
                      showForm={true}
                      handleRemove={this.unassignAttachmentFromTemplate}
                    />
                  </Panel>
                </Collapse>
              </Col>
            </Row>
            <Row>
              <Col>
                <Descriptions>
                  <Descriptions.Item label={<b>Recipient Contact Levels</b>}>
                    {contactLevel}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col>
                <Descriptions>
                  <Descriptions.Item label={<b>Recipient CC Contact Levels</b>}>
                    {ccContactLevel}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
